import { useQuery } from '@apollo/client'
import { GET_AD_HISTORY_WINNERS } from '@/graphql/ad-history-queries'
import type { AdHistoryWinner, AdHistoryWinners } from '@/components/campaigns/adHistory/types'

export interface WinningCopy {
    /** The key of the item generation uses; stable while the copy and the filter stay the same */
    key: string
    headline: string | null
    primaryText: string | null
}

export interface WinningCopyState {
    /** Winning copy that generation uses now, best first: the cleaned text generation sees, never the stored text */
    copies: WinningCopy[]
    /** The licence chose automated mode and staff allow it */
    automated: boolean
    loading: boolean
}

const textOf = (value: string | null | undefined) => (typeof value === 'string' && value.trim() ? value.trim() : null)

const usedCopyOf = (winner: AdHistoryWinner): WinningCopy | null => {
    const headline = textOf(winner.headline)
    const primaryText = textOf(winner.primaryText)
    if (winner.state !== 'used' || !winner.key || (!headline && !primaryText)) return null
    return { key: winner.key, headline, primaryText }
}

/**
 * The winning ad copy of the licence's own ads that generation uses now, for the ad content step. Only items in the
 * state "used" are offered (what generation sees, after the filter); a copy that is proposed, excluded, not offered
 * or cannot be used is never listed. A failed read gives no copy: the step works as it did without this.
 */
export const useWinningCopy = (campaignId: string | null | undefined): WinningCopyState => {
    const { data, loading } = useQuery<{ adHistoryWinners: AdHistoryWinners }>(GET_AD_HISTORY_WINNERS, {
        variables: { campaignId, purpose: 'CREATIVE_PROMPTS' },
        skip: !campaignId,
        fetchPolicy: 'network-only',
        errorPolicy: 'ignore',
    })
    const winners = data?.adHistoryWinners
    if (!winners?.enabled) return { copies: [], automated: false, loading: !!loading }
    return {
        copies: winners.winners.map(usedCopyOf).filter((copy): copy is WinningCopy => copy !== null),
        automated: winners.mode === 'automated',
        loading: !!loading,
    }
}

/**
 * The variations with `text` added: it fills the first empty variation, else it is added as a new one while there is
 * room. Text the user typed is never replaced. Null when there is nothing to do (no text, already there, no room).
 */
export const withCopy = (variations: string[], text: string | null, max: number): string[] | null => {
    const value = textOf(text)
    if (!value) return null
    const current = Array.isArray(variations) && variations.length > 0 ? variations : ['']
    if (current.some((variation) => variation.trim() === value)) return null
    const empty = current.findIndex((variation) => variation.trim() === '')
    if (empty >= 0) return current.map((variation, index) => (index === empty ? value : variation))
    return current.length < max ? [...current, value] : null
}
